import type { UserRole } from '../../services/users.service';
import { canonicalRole, roleInfo } from './roleCatalog';

// Mirrors the api-gateway: TenantMiddleware reduces the token roles to one
// caller role by this priority, and routes/admin.py get_assignable_roles_for_request
// decides what that role may assign on create/role change. POST /invitations
// grants the same sets (routes/beta_invitations.py).
const REQUEST_ROLE_PRIORITY = [
  'superadmin',
  'superuser',
  'platform-superadmin',
  'super-admin',
  'platform-admin',
  'admin',
  'tenant-admin',
  'tenant_admin',
  'agent-developer',
  'agent_developer',
  'developer',
  'analyst',
  'partner',
  'viewer',
  'compliance_officer',
  'compliance-officer',
  'management',
  'auditor',
  'user',
];
const SUPERADMIN_REQUEST_ROLES = ['superadmin', 'superuser', 'platform-superadmin', 'super-admin'];
const REQUEST_ROLE_ALIASES: Record<string, string> = {
  'tenant-admin': 'tenant_admin',
  platform_admin: 'platform-admin',
  super_admin: 'super-admin',
};

const TENANT_ASSIGNABLE_ROLES: AssignableRole[] = ['tenant_admin', 'user', 'viewer', 'compliance_officer', 'auditor', 'management'];
const PLATFORM_ASSIGNABLE_ROLES: AssignableRole[] = [
  'admin',
  'user',
  'tenant_admin',
  'agent_developer',
  'analyst',
  'viewer',
  'partner',
  'compliance_officer',
  'auditor',
  'management',
];

// Least rights first. `admin` is the self-signup spelling of tenant_admin: the
// server treats both alike, so the selects offer it only where tenant_admin is
// not on offer.
const ROLE_OPTION_ORDER: AssignableRole[] = [
  'viewer',
  'auditor',
  'management',
  'user',
  'compliance_officer',
  'analyst',
  'agent_developer',
  'partner',
  'admin',
  'tenant_admin',
  'superadmin',
];

/** The roles a caller can hand out: the users.role enum minus legacy spellings. */
export type AssignableRole = UserRole | 'compliance_officer' | 'auditor' | 'management';

export interface RoleOption {
  value: AssignableRole;
  labelKey: string;
  fallback: string;
}

export function resolveCallerRole(tokenRoles: string[]): string | null {
  const lowered = tokenRoles.map((role) => role.toLowerCase());
  const prioritized = REQUEST_ROLE_PRIORITY.find((role) => lowered.includes(role));
  return prioritized ?? tokenRoles[0] ?? null;
}

export function getAssignableRoles(tokenRoles: string[]): AssignableRole[] {
  const callerRole = resolveCallerRole(tokenRoles);
  if (!callerRole) return [];
  if (SUPERADMIN_REQUEST_ROLES.includes(callerRole)) {
    return [...PLATFORM_ASSIGNABLE_ROLES, 'superadmin'];
  }
  const lowered = callerRole.trim().toLowerCase();
  const normalized = REQUEST_ROLE_ALIASES[lowered] ?? lowered;
  if (normalized === 'platform-admin') return [...PLATFORM_ASSIGNABLE_ROLES];
  if (normalized === 'admin' || normalized === 'tenant_admin') return [...TENANT_ASSIGNABLE_ROLES];
  return [];
}

export function roleOption(role: string): RoleOption {
  const label = roleInfo(role)?.label;
  return {
    value: role as AssignableRole,
    labelKey: label?.key ?? `users.roles.${role}`,
    fallback: label?.fallback ?? role.replace(/[_-]+/g, ' '),
  };
}

export function getAssignableRoleOptions(tokenRoles: string[]): RoleOption[] {
  const assignable = new Set(getAssignableRoles(tokenRoles));
  if (assignable.has('tenant_admin')) assignable.delete('admin');
  return ROLE_OPTION_ORDER.filter((role) => assignable.has(role)).map(roleOption);
}

/** The value a role select shows for a stored role; `admin` shows as tenant_admin. */
export function normalizeEditableRole(role?: string | null): string {
  const normalized = canonicalRole(role);
  return normalized === 'admin' ? 'tenant_admin' : normalized;
}

/** Options for changing a user's role: the current role stays visible but is not selectable if the caller may not grant it. */
export function roleOptionsForEdit(
  options: RoleOption[],
  currentRole: string,
): Array<RoleOption & { disabled?: boolean }> {
  if (!currentRole || options.some((option) => option.value === currentRole)) return options;
  return [{ ...roleOption(currentRole), disabled: true }, ...options];
}

const ROLE_REFUSAL = /^Role '([^']+)' (?:cannot be assigned|can only be granted)/;

/** The role named in the api-gateway's 403 refusal for a role the caller may not grant. */
export function refusedRoleFromDetail(detail: unknown): string | null {
  if (typeof detail !== 'string') return null;
  return ROLE_REFUSAL.exec(detail)?.[1] ?? null;
}
